import { MapPin, Phone, Navigation } from "lucide-react";
import { DOCTOR, mapsEmbedSrc, mapsDirectionsLink } from "@/config/doctorConfig";
import { SectionHeading, Reveal } from "@/components/motion";

export const Location = () => (
  <section id="contact" className="bg-white border-y border-slate-200/70 py-16 sm:py-24 lg:py-32" data-testid="location-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        number="07"
        eyebrow="Visit"
        title="Visit Dr. Suyash Bhadoriya"
        sub="Conveniently located in Saket Nagar, Bhopal — near AIIMS Gate No. 3, Habib Ganj."
      />
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-5">
          <address className="not-italic h-full bg-slate-50/70 rounded-3xl border border-slate-200/80 p-7 sm:p-9 flex flex-col" data-testid="clinic-address-card">
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-xl bg-navy-900 flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-teal-300" />
              </span>
              <div>
                <h3 className="font-serif font-semibold text-slate-900 text-xl">{DOCTOR.hospitalName}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {DOCTOR.addressLine1}<br />
                  {DOCTOR.addressLine2}<br />
                  {DOCTOR.addressLine3}
                </p>
              </div>
            </div>
            <div className="mt-7 pt-6 border-t border-slate-200 space-y-3 text-sm">
              <p className="flex justify-between gap-4">
                <span className="text-slate-500 font-mono text-xs uppercase tracking-wider pt-0.5">OPD Timings</span>
                <span className="font-semibold text-navy-900 text-right" data-testid="location-opd-timings">
                  {DOCTOR.opdDays}: {DOCTOR.opdTime}
                  <span className="block text-xs font-normal text-slate-500">{DOCTOR.opdSunday} · {DOCTOR.opdOff}</span>
                </span>
              </p>
              <p className="flex justify-between gap-4">
                <span className="text-slate-500 font-mono text-xs uppercase tracking-wider pt-0.5">Clinic</span>
                <a href={DOCTOR.clinicPhoneHref} data-testid="location-clinic-phone" className="font-semibold text-navy-900 hover:text-teal-700 transition-colors">{DOCTOR.clinicPhoneDisplay}</a>
              </p>
              <p className="flex justify-between gap-4">
                <span className="text-slate-500 font-mono text-xs uppercase tracking-wider pt-0.5">Mobile</span>
                <a href={DOCTOR.mobileHref} data-testid="location-mobile-phone" className="font-semibold text-navy-900 hover:text-teal-700 transition-colors">{DOCTOR.mobileDisplay}</a>
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={mapsDirectionsLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-navy-900 hover:bg-navy-700 transition-colors duration-200"
              >
                <Navigation size={15} /> Get Directions
              </a>
              <a
                href={DOCTOR.googleProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-google-profile-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors duration-200"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                View on Google
              </a>
              <a
                href={DOCTOR.clinicPhoneHref}
                data-testid="location-call-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-navy-900 border border-slate-300 hover:border-teal-600 hover:text-teal-700 transition-colors duration-200"
              >
                <Phone size={15} /> Call Clinic
              </a>
            </div>
          </address>
        </Reveal>
        <Reveal delay={1} className="lg:col-span-7">
          <div className="h-full min-h-[360px] rounded-3xl overflow-hidden border border-slate-200/80 shadow-soft" data-testid="google-map-embed">
            <iframe
              title="Map — Shree Multispeciality Hospital, Saket Nagar, Bhopal"
              src={mapsEmbedSrc()}
              className="w-full h-full min-h-[360px] border-0"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
